import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { fuzzyFilterAndSort } from '../../utils/fuzzySearch';

export interface DropdownOption {
  value: string;
  label: string;
}

interface FixedDropdownPickerProps {
  id: string;
  ariaLabelledBy?: string;
  value: string | null;
  options: readonly DropdownOption[];
  placeholder: string;
  disabled?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Shown on the trigger when `value` is not in `options` (e.g. custom “Other” text). */
  selectedLabel?: string | null;
  /** Always listed after search results (e.g. N/A, Other). */
  footerOptions?: readonly DropdownOption[];
  onChange: (value: string) => void;
}

function ChevronDown({ open }: { open: boolean }) {
  return (
    <svg
      className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="h-4 w-4 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

export default function FixedDropdownPicker({
  id,
  ariaLabelledBy,
  value,
  options,
  placeholder,
  disabled = false,
  searchable = false,
  searchPlaceholder = 'Search…',
  selectedLabel,
  footerOptions = [],
  onChange,
}: FixedDropdownPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find((option) => option.value === value);

  const searchableOptions = useMemo(() => {
    const footerValues = new Set(footerOptions.map((option) => option.value));
    return options.filter((option) => !footerValues.has(option.value));
  }, [options, footerOptions]);

  const visibleOptions = useMemo(() => {
    const footer = [...footerOptions];
    if (!searchable || !searchQuery.trim()) {
      return [...searchableOptions, ...footer];
    }
    const filtered = fuzzyFilterAndSort(searchableOptions, searchQuery, (option) => option.label);
    return [...filtered, ...footer];
  }, [searchableOptions, footerOptions, searchable, searchQuery]);

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const preferredHeight = searchable ? 320 : 280;
    const openDown = spaceBelow >= 160 || spaceBelow >= spaceAbove;

    if (openDown) {
      setMenuStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        top: rect.bottom + 4,
        maxHeight: Math.max(120, Math.min(preferredHeight, spaceBelow - 12)),
        overflowY: 'auto',
        zIndex: 60,
      });
      return;
    }

    setMenuStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      bottom: window.innerHeight - rect.top + 4,
      maxHeight: Math.max(120, Math.min(preferredHeight, spaceAbove - 12)),
      overflowY: 'auto',
      zIndex: 60,
    });
  }, [searchable]);

  useLayoutEffect(() => {
    if (isOpen) updateMenuPosition();
  }, [isOpen, updateMenuPosition, visibleOptions.length]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setIsOpen(false);
      setSearchQuery('');
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopImmediatePropagation();
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape, true);
    document.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape, true);
      document.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (isOpen && searchable) {
      const timer = window.setTimeout(() => searchRef.current?.focus(), 0);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [isOpen, searchable]);

  const openMenu = () => {
    if (disabled) return;
    setIsOpen((open) => !open);
    if (isOpen) setSearchQuery('');
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        id={id}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={ariaLabelledBy}
        disabled={disabled}
        onClick={openMenu}
        className={`flex w-full items-center justify-between rounded-md border bg-white px-3 py-2.5 text-base transition-colors focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-400 dark:bg-gray-950 dark:text-gray-100 dark:focus:border-gray-400 ${
          disabled
            ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400 dark:border-white/10 dark:bg-gray-900 dark:text-gray-500'
            : isOpen
              ? 'border-gray-500 text-gray-900 ring-1 ring-gray-400 dark:border-white/30'
              : 'border-gray-300 text-gray-900 hover:border-gray-400 dark:border-white/15'
        }`}
      >
        <span className={`truncate text-left ${selectedOption || selectedLabel ? '' : 'text-gray-500'}`}>
          {selectedLabel ?? selectedOption?.label ?? placeholder}
        </span>
        <ChevronDown open={isOpen} />
      </button>

      {isOpen && (
        <div
          ref={menuRef}
          style={menuStyle}
          className="flex flex-col overflow-hidden rounded-md border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-950 shadow-lg"
        >
          {searchable && (
            <div className="shrink-0 border-b border-gray-200 dark:border-white/10 bg-white dark:bg-gray-950 p-2">
              <input
                ref={searchRef}
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="w-full rounded-md border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-950 px-3 py-2 text-base text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-400"
                onKeyDown={(event) => event.stopPropagation()}
              />
            </div>
          )}
          <ul role="listbox" aria-labelledby={ariaLabelledBy} className="min-h-0 flex-1 overflow-y-auto">
            {visibleOptions.length === 0 ? (
              <li className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">No matches found.</li>
            ) : (
              visibleOptions.map((option) => {
                const isSelected = option.value === value;
                return (
                  <li key={option.value} role="option" aria-selected={isSelected}>
                    <button
                      type="button"
                      onClick={() => {
                        onChange(option.value);
                        setIsOpen(false);
                        setSearchQuery('');
                      }}
                      className={`flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-base transition-colors ${
                        isSelected ? 'bg-gray-100 font-medium text-gray-900 dark:bg-gray-800 dark:text-white' : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-white/5'
                      }`}
                    >
                      <span>{option.label}</span>
                      {isSelected && <CheckIcon />}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
